import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { MapPin, Search, Plus, Check, ExternalLink, Loader2 } from 'lucide-react';
import { apiBase, authHeaders, getToken, resolveOrgId } from '../utils/authHeaders';
import { errorMessage, isCancelled } from './MarketplaceShared';

// "Find local suppliers": public registers (FSA hygiene ratings, Companies House)
// searched around a site. Nothing is added until a person presses "Add as supplier".
export const vdApi = axios.create({ baseURL: `${apiBase()}/api/vendor-directory` });

export const RADII = [5, 10, 25];
export const KIND_LABELS = {
  distributor: 'Wholesalers / distributors',
  manufacturer: 'Manufacturers / packers',
  importer: 'Importers / exporters',
  retailer: 'Other retailers',
};
const DEFAULT_KINDS = ['distributor', 'manufacturer', 'importer'];

const readLocation = () => { try { return localStorage.getItem('current_location_id') || ''; } catch { return ''; } };
const fmtDate = iso => (iso ? String(iso).slice(0, 10) : '');
const fmtKm = d => (d === null || d === undefined ? '—' : `${Number(d).toFixed(1)} km`);

function hygiene(row) {
  const rating = row.evidence?.fhrs_rating;
  if (rating === undefined || rating === null || rating === '') return '—';
  return `${rating}${row.evidence?.fhrs_rating_date ? ` (${fmtDate(row.evidence.fhrs_rating_date)})` : ''}`;
}

// From Companies House SIC codes (server-side). FSA's "distributor" type also covers
// delivery-only takeaways, so this is the badge that separates wholesalers from them.
const TRADE = {
  food_wholesale: { label: 'Food wholesaler', cls: 'text-green-700 bg-green-50' },
  food_service: { label: 'Restaurant / takeaway', cls: 'text-amber-700 bg-amber-50' },
  other: { label: 'Other trade', cls: 'text-ink-muted bg-surface-sunken' },
  unknown: { label: 'Trade unknown', cls: 'text-ink-muted bg-surface-sunken' },
};

export function tradeBadge(row) {
  return TRADE[row.trade] || TRADE.unknown;
}

function companyStatus(row) {
  const ch = row.provider === 'companies_house' ? row.evidence : row.evidence?.companies_house;
  return ch?.company_status || '—';
}

function sourceLinks(row) {
  const links = [];
  if (row.source_url) links.push({ href: row.source_url, label: row.provider === 'companies_house' ? 'Companies House' : 'FSA' });
  const ch = row.evidence?.companies_house;
  if (ch?.source_url) links.push({ href: ch.source_url, label: 'Companies House' });
  return links;
}

export default function VendorDiscovery({ onImported }) {
  const [available, setAvailable] = useState(false);
  const [providers, setProviders] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState(readLocation);
  const [radius, setRadius] = useState(10);
  const [kinds, setKinds] = useState(DEFAULT_KINDS);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState('');
  const [error, setError] = useState('');
  const controller = useRef(null);
  const mounted = useRef(true);

  const config = useCallback(extra => ({
    headers: authHeaders({ orgId: resolveOrgId(), token: getToken(), locationId: readLocation() }),
    ...extra,
  }), []);

  useEffect(() => {
    mounted.current = true;
    const ctl = new AbortController();
    controller.current = ctl;
    vdApi.get('/providers', config({ signal: ctl.signal }))
      .then(res => {
        if (ctl.signal.aborted) return;
        setProviders((res?.data?.providers || []).filter(p => p.health?.enabled));
        setAvailable(true);
        return vdApi.get('/api/locations', config({ signal: ctl.signal, baseURL: apiBase() }))
          .then(loc => {
            if (ctl.signal.aborted || !Array.isArray(loc?.data)) return;
            setSites(loc.data);
            setSiteId(current => current || loc.data[0]?.id || '');
          })
          .catch(() => {});   // site list is a convenience; the current site still works
      })
      .catch(() => {});       // module off or no access: the panel simply is not shown
    return () => { mounted.current = false; ctl.abort(); };
  }, [config]);

  const toggleKind = kind => setKinds(ks => (ks.includes(kind) ? ks.filter(k => k !== kind) : [...ks, kind]));

  const search = async event => {
    event?.preventDefault();
    if (!kinds.length) { setError('Choose at least one kind of supplier.'); return; }
    setBusy(true); setError('');
    try {
      const res = await vdApi.get('/search', config({
        signal: controller.current?.signal,
        params: { location_id: siteId || undefined, radius_km: radius, kinds: kinds.join(','), q: query.trim() || undefined },
      }));
      if (!mounted.current) return;
      setResults(res.data?.results || []);
      setWarnings((res.data?.providers || []).filter(p => !p.ok));
    } catch (e) {
      if (mounted.current && !isCancelled(e)) setError(errorMessage(e));
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const add = async row => {
    const key = `${row.provider}:${row.external_id}`;
    setAdding(key); setError('');
    try {
      const res = await vdApi.post('/import', {
        provider: row.provider, external_id: row.external_id, name: row.name,
        address: row.address, postcode: row.postcode, evidence: row.evidence,
      }, config({ signal: controller.current?.signal }));
      if (!mounted.current) return;
      const vendorId = res.data?.vendor?.id;
      setResults(rs => rs.map(r => (r === row ? { ...r, already_vendor: true, vendor_id: vendorId } : r)));
      if (onImported) onImported(res.data?.vendor);
    } catch (e) {
      if (mounted.current && !isCancelled(e)) setError(errorMessage(e));
    } finally {
      if (mounted.current) setAdding('');
    }
  };

  if (!available) return null;

  const currentId = readLocation();
  const siteOptions = sites.length ? sites : (currentId ? [{ id: currentId, name: 'Current site' }] : []);

  return (
    <section className="bg-white rounded-xl shadow-sm border border-surface-border p-5 mb-6" data-testid="vendor-discovery" aria-label="Find local suppliers">
      <div className="flex items-center gap-2 mb-1"><MapPin className="w-5 h-5 text-primary-500" /><h2 className="font-display text-xl text-ink">Find local suppliers</h2></div>
      <p className="text-xs text-ink-muted mb-4">
        Searches public registers ({providers.map(p => p.id === 'fsa_fhrs' ? 'FSA food hygiene ratings' : p.id === 'companies_house' ? 'Companies House' : p.id).join(', ') || 'none enabled'}).
        Ratings and company status are shown as published; check them before you order.
      </p>
      <form onSubmit={search} className="flex flex-wrap items-end gap-3 mb-4" aria-label="Supplier search">
        <label className="text-xs text-ink-muted">Site
          <select aria-label="Site" value={siteId} onChange={e => setSiteId(e.target.value)} className="block mt-1 border border-surface-border rounded-md px-2 py-1.5 text-sm text-ink">
            {siteOptions.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
          </select>
        </label>
        <label className="text-xs text-ink-muted">Within
          <select aria-label="Radius" value={radius} onChange={e => setRadius(Number(e.target.value))} className="block mt-1 border border-surface-border rounded-md px-2 py-1.5 text-sm text-ink">
            {RADII.map(r => <option key={r} value={r}>{r} km</option>)}
          </select>
        </label>
        <label className="text-xs text-ink-muted">Name contains
          <input aria-label="Name contains" value={query} onChange={e => setQuery(e.target.value)} maxLength={100} className="block mt-1 border border-surface-border rounded-md px-2 py-1.5 text-sm text-ink" />
        </label>
        <fieldset className="flex flex-wrap gap-3 text-sm text-ink">
          <legend className="sr-only">Kinds of supplier</legend>
          {Object.entries(KIND_LABELS).map(([kind, label]) => (
            <label key={kind} className="flex items-center gap-1">
              <input type="checkbox" aria-label={label} checked={kinds.includes(kind)} onChange={() => toggleKind(kind)} /> {label}
            </label>
          ))}
        </fieldset>
        <button type="submit" disabled={busy} className="flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Search
        </button>
      </form>
      {error && <div role="alert" className="text-sm text-red-700 mb-3">{error}</div>}
      {warnings.map(w => <div key={w.id} className="text-xs text-amber-700 mb-2" data-testid={`vendor-discovery-warning-${w.id}`}>{w.id === 'companies_house' ? 'Companies House' : w.id === 'fsa_fhrs' ? 'FSA' : w.id} did not answer; results may be incomplete.</div>)}
      {results && (results.length === 0
        ? <div className="text-sm text-ink-muted" data-testid="vendor-discovery-empty">No matching businesses found. Try a wider radius or more kinds.</div>
        : (
          <table className="w-full text-sm" data-testid="vendor-discovery-results">
            <thead className="bg-surface-sunken text-ink-muted text-xs uppercase"><tr>
              <th className="text-left px-3 py-2">Name</th><th className="text-right px-3 py-2">Distance</th>
              <th className="text-left px-3 py-2">Hygiene rating</th><th className="text-left px-3 py-2">Company status</th>
              <th className="text-left px-3 py-2">Source</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>{results.map(row => {
              const key = `${row.provider}:${row.external_id}`;
              return (
                <tr key={key} className="border-t border-surface-border" data-testid={`vendor-discovery-row-${key}`}>
                  <td className="px-3 py-2"><div className="font-medium text-ink">{row.name} <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${tradeBadge(row).cls}`} data-testid="trade-badge">{tradeBadge(row).label}</span></div><div className="text-xs text-ink-muted">{[row.address, row.postcode].filter(Boolean).join(', ')}</div></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtKm(row.distance_km)}</td>
                  <td className="px-3 py-2">{hygiene(row)}</td>
                  <td className="px-3 py-2">{companyStatus(row)}</td>
                  <td className="px-3 py-2">{sourceLinks(row).map(l => (
                    <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary-600 hover:underline text-xs mr-2">{l.label}<ExternalLink className="w-3 h-3" /></a>
                  ))}</td>
                  <td className="px-3 py-2 text-right">{row.already_vendor
                    ? <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded" data-testid="already-supplier"><Check className="w-3 h-3" />Already a supplier</span>
                    : <button type="button" onClick={() => add(row)} disabled={adding === key} className="inline-flex items-center gap-1 text-primary-600 border border-primary-500 px-2 py-1 rounded text-xs font-medium hover:bg-primary-50 disabled:opacity-60"><Plus className="w-3 h-3" />Add as supplier</button>}
                  </td>
                </tr>
              );
            })}</tbody>
          </table>
        ))}
    </section>
  );
}
